import { Controller } from "@hotwired/stimulus"

// "Find your company" combobox for /for-roofers. Debounced fetch to
// /roofers/lookup, renders a keyboard-accessible ARIA combobox listbox.
// Unclaimed → claim wizard; claimed → a muted "already claimed" affordance;
// always a persistent "Don't see your company? Add it" → signup.
//
// Modeled on guided_search_controller's fetch/render scaffolding, but renders
// results via DOM node construction (never innerHTML with server data) so a
// business name can't inject markup.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    lookupUrl: String,   // /roofers/lookup
    claimUrlTemplate: String, // /claim/:slug (":slug" replaced)
    signupUrl: String,   // /roofers/signup
    contactUrl: String   // /contact — where an already-claimed owner disputes
  }

  connect() {
    this.activeIndex = -1
    this.items = []
    this.reqSeq = 0 // monotonic token so a stale out-of-order response can't overwrite newer results
    this.onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    clearTimeout(this.timeout)
  }

  // Debounced input handler.
  search() {
    clearTimeout(this.timeout)
    const q = this.inputTarget.value.trim()
    if (q.length < 2) { this.close(); return }
    this.timeout = setTimeout(() => this.fetchResults(q), 250)
  }

  async fetchResults(q) {
    // Per-call token: if the user keeps typing, a newer request bumps reqSeq and
    // this stale response bails before rendering (typeahead out-of-order guard).
    const reqId = ++this.reqSeq
    try {
      const resp = await fetch(`${this.lookupUrlValue}?q=${encodeURIComponent(q)}`, {
        headers: { "Accept": "application/json" }
      })
      if (reqId !== this.reqSeq) return
      if (!resp.ok) { this.close(); return }
      const data = await resp.json()
      if (reqId !== this.reqSeq) return
      this.items = data
      this.render()
    } catch (_e) {
      if (reqId === this.reqSeq) this.close()
    }
  }

  render() {
    const list = this.resultsTarget
    list.replaceChildren()
    this.activeIndex = -1
    // Clear the stale ARIA pointer — new rows reuse index-based ids, so a leftover
    // aria-activedescendant would dangle or point at a different business.
    this.inputTarget.removeAttribute("aria-activedescendant")

    this.items.forEach((biz, i) => list.appendChild(this.buildRow(biz, i)))
    list.appendChild(this.buildAddItRow())

    list.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  buildRow(biz, index) {
    // Both branches are real anchors so keyboard Enter (which calls .click()) and
    // mouse both navigate — no silent dead-end row. Unclaimed → claim wizard;
    // claimed → the contact page (an owner disputing a mis-scraped/stale claim).
    const row = document.createElement("a")
    row.className = "block px-4 py-3 border-b border-emerald-50 hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none cursor-pointer"
    row.setAttribute("role", "option")
    row.id = `company-option-${index}`
    row.dataset.index = index

    if (!biz.claimed) {
      row.href = this.claimUrlTemplateValue.replace(":slug", encodeURIComponent(biz.slug))
    } else {
      row.href = `${this.contactUrlValue}?slug=${encodeURIComponent(biz.slug)}`
      row.classList.add("opacity-70")
    }

    // name · city, state · rating — all textContent, never innerHTML.
    const name = document.createElement("span")
    name.className = "font-semibold text-gray-900"
    name.textContent = biz.name
    row.appendChild(name)

    const meta = document.createElement("span")
    meta.className = "block text-sm text-gray-500"
    const locale = [biz.city, biz.state].filter(Boolean).join(", ")
    const rating = biz.rating ? ` · ★ ${biz.rating}` : ""
    meta.textContent = `${locale}${rating}`
    row.appendChild(meta)

    const chip = document.createElement("span")
    if (biz.claimed) {
      chip.className = "inline-block mt-1 text-xs font-medium text-gray-500"
      chip.textContent = "Already claimed — this is my business? Contact us"
    } else {
      chip.className = "inline-block mt-1 text-xs font-medium text-emerald-700"
      chip.textContent = "Claim this profile →"
    }
    row.appendChild(chip)

    return row
  }

  buildAddItRow() {
    const add = document.createElement("a")
    add.href = this.signupUrlValue
    add.className = "block px-4 py-3 bg-emerald-50 text-emerald-800 font-medium hover:bg-emerald-100"
    add.setAttribute("role", "option")
    add.textContent = "Don't see your company? Add it →"
    return add
  }

  // Keyboard: ↑/↓ move, Enter activates, Escape closes.
  keydown(e) {
    const options = Array.from(this.resultsTarget.querySelectorAll('[role="option"]'))
    if (this.resultsTarget.classList.contains("hidden") || options.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.move(options, 1)
        break
      case "ArrowUp":
        e.preventDefault()
        this.move(options, -1)
        break
      case "Enter":
        if (this.activeIndex >= 0 && options[this.activeIndex]) {
          e.preventDefault()
          options[this.activeIndex].click()
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  move(options, delta) {
    if (this.activeIndex >= 0 && options[this.activeIndex]) {
      options[this.activeIndex].classList.remove("bg-emerald-50")
    }
    this.activeIndex = (this.activeIndex + delta + options.length) % options.length
    const active = options[this.activeIndex]
    active.classList.add("bg-emerald-50")
    active.scrollIntoView({ block: "nearest" })
    this.inputTarget.setAttribute("aria-activedescendant", active.id || "")
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.replaceChildren()
    this.activeIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }
}
